import { useForm } from "@inertiajs/react";
import axios from "axios";
import { useEffect, useState } from "react";

import FormModal from "@/components/Modal/FormModal";
import SearchSelect from "@/components/SearchSelect";
import TextFieldComponent from "@/components/TextField";
import { formatCurrencyInput, parseCurrencyInput } from "@/utils/currencyInput";
import { completMonth } from "@/utils/dateFormater";

type Props = {
  mode: "create" | "edit";
  consumers: any[];
  consumerOptions: any[];
  initialConsumerId?: number | string | null;
  filters: { year: string; month: string };
  expense?: any;
  consumerName?: string;
  onClose: () => void;
  onSaved: (message: string) => void;
  onEditExisting?: (expense: any, name: string) => void;
};

export default function EnergyExpenseModal({
  mode,
  consumers,
  consumerOptions,
  initialConsumerId,
  filters,
  expense,
  consumerName,
  onClose,
  onSaved,
  onEditExisting,
}: Props) {
  const editing = mode === "edit";
  const [consumerId, setConsumerId] = useState("");
  const [existing, setExisting] = useState<any>(null);
  const createForm = useForm({
    kwh: "",
    compensated_kwh: "",
    price: "",
    due_date: "",
  });
  const editForm = useForm({
    price: formatCurrencyInput(expense?.price),
    due_date: expense?.due_date || "",
  });
  const form: any = editing ? editForm : createForm;

  useEffect(() => {
    if (!editing) setConsumerId(initialConsumerId ? String(initialConsumerId) : "");
  }, [editing, initialConsumerId]);

  useEffect(() => {
    setExisting(null);
    if (editing || !consumerId) return;

    let active = true;
    axios
      .get("/manager/accounts/check", {
        params: {
          consumer_id: consumerId,
          year: filters.year,
          month: completMonth.indexOf(filters.month) + 1,
        },
      })
      .then(({ data }) => active && data.exists && setExisting(data));
    return () => {
      active = false;
    };
  }, [consumerId, editing, filters]);

  const submit = () => {
    if (editing) {
      editForm.transform((data) => ({ ...data, price: parseCurrencyInput(data.price) }));
      editForm.patch(`/manager/accounts/${expense.id}`, {
        onSuccess: () => onSaved("Conta Geedee atualizada com sucesso."),
      });
      return;
    }
    createForm.transform((data) => ({
      ...data,
      consumer_id: consumerId,
      year: filters.year,
      month: String(completMonth.indexOf(filters.month) + 1),
      price: parseCurrencyInput(data.price),
    }));
    createForm.post("/manager/accounts", {
      onSuccess: () =>
        onSaved(`Conta Geedee criada com sucesso para ${filters.month} de ${filters.year}.`),
    });
  };

  const selectedExpense =
    existing && consumers.find((consumer) => consumer.id === existing.expense_id);

  return (
    <FormModal
      isOpen
      onClose={onClose}
      title={editing ? "Editar conta Geedee" : "Criar conta Geedee"}
      description={
        <span>
          {editing ? `${consumerName} · ` : "Preencha os dados da conta para continuar. · "}
          Referência: {filters.month} de {filters.year}
        </span>
      }
      submitButtonTitle={editing ? "Salvar edição" : "Criar conta"}
      onSubmit={submit}
      isSubmitting={form.processing}
      submitDisabled={Boolean(existing)}
      size="sm"
    >
      <div className="grid grid-cols-1 gap-4 md:gap-6">
        {!editing && (
          <>
            <SearchSelect
              label="Consumidor"
              options={consumerOptions}
              value={consumerId}
              onChange={setConsumerId}
              placeholder="Selecione um consumidor"
              required
              error={Boolean(existing)}
              errorMessage={
                existing
                  ? existing.approved
                    ? `${existing.consumer_name} já possui uma conta criada este mês. Como está aprovada, não é possível editá-la.`
                    : `${existing.consumer_name} já possui uma conta criada este mês. Deseja editar?`
                  : undefined
              }
            />
            {existing && !existing.approved && selectedExpense && (
              <button
                type="button"
                className="-mt-4 ml-4 w-fit text-xs font-semibold text-red-500 underline"
                onClick={() => {
                  onClose();
                  onEditExisting?.(selectedExpense, existing.consumer_name);
                }}
              >
                Editar conta
              </button>
            )}
            <TextFieldComponent
              labelPosition="top"
              label="KWh consumidos"
              required
              type="number"
              placeholder="Ex.: 200"
              value={createForm.data.kwh}
              onChange={(event) => createForm.setData("kwh", event.target.value)}
            />
            <TextFieldComponent
              labelPosition="top"
              label="Energia compensada (kWh)"
              optional
              type="number"
              placeholder="Ex.: 150"
              value={createForm.data.compensated_kwh}
              onChange={(event) => createForm.setData("compensated_kwh", event.target.value)}
            />
          </>
        )}
        <TextFieldComponent
          labelPosition="top"
          label="Preço total (R$)"
          required
          type="text"
          inputMode="decimal"
          mask="currency"
          placeholder="R$ 0,00"
          value={form.data.price}
          onChange={(event) => form.setData("price", event.target.value)}
        />
        <TextFieldComponent
          labelPosition="top"
          label="Data de vencimento"
          required
          type="date"
          placeholder="Selecione a data"
          value={form.data.due_date}
          onChange={(event) => form.setData("due_date", event.target.value)}
        />
      </div>
    </FormModal>
  );
}
